/**
 * Stale-chunk detection and recovery.
 *
 * A tab whose session straddles a frontend deploy still holds the previous
 * build's `index.html`, which references route chunks by content hash. Those
 * exact filenames are not part of the new deployment, so the next lazy
 * navigation requests a file the origin no longer has. The SPA rewrite answers
 * with `index.html` (200, `text/html`) instead of a 404, the browser fails the
 * module MIME check, and the dynamic `import()` rejects.
 *
 * The client cannot repair this in place — the running document is simply out of
 * date. The only real recovery is to load the new document, so we reload. The
 * reload is windowed rather than unconditional: if a deploy is genuinely broken
 * (the new build 404s too), an unguarded reload-on-failure is an infinite
 * refresh loop that is far worse than the error it replaced.
 *
 * Reloading here costs the user nothing extra. The failure happens during a
 * navigation, which has already unmounted the page they were on, so there is no
 * in-progress form state left to lose.
 */

/** Marks the last automatic recovery so a broken deploy cannot become a loop. */
const RELOAD_MARKER_KEY = "nexus.stale-chunk-reload-at"

/**
 * How long a recovery attempt suppresses the next one. Long enough that a
 * failing deploy trips the guard on its immediate retry, short enough that a
 * long-lived tab straddling a second, unrelated deploy still self-heals.
 */
const RELOAD_COOLDOWN_MS = 30_000

/**
 * Substrings identifying a failed module load, lowercased for comparison.
 *
 * Each engine words this differently and none of them is stable enough to parse
 * structurally — there is no error code for it, so message matching is the only
 * option available. Matching too broadly here costs a spurious reload; matching
 * too narrowly leaves the user on the raw error page, so the list errs wide.
 */
const STALE_CHUNK_MESSAGES = [
  // Chrome / Edge. Also how Chrome surfaces the MIME rejection on import().
  "failed to fetch dynamically imported module",
  // Firefox
  "error loading dynamically imported module",
  // Safari
  "importing a module script failed",
  // When the browser reports the MIME mismatch as the error itself
  "expected a javascript module script but the server responded with a mime type",
  // Vite's CSS preload path has its own wording
  "unable to preload css",
  // webpack-era phrasing, still emitted by some third-party chunks
  "loading chunk",
  "chunkloaderror"
]

function extractMessage(error: unknown): string {
  if (typeof error === "string") return error
  if (error instanceof Error) return `${error.name}: ${error.message}`
  if (typeof error === "object" && error !== null) {
    const { name, message } = error as { name?: unknown; message?: unknown }
    if (typeof message === "string") {
      return typeof name === "string" ? `${name}: ${message}` : message
    }
  }
  return ""
}

/**
 * True when `error` looks like a module/chunk that failed to load — i.e. the
 * running document is referencing assets the server no longer serves.
 */
export function isStaleChunkError(error: unknown): boolean {
  const message = extractMessage(error).toLowerCase()
  if (!message) return false
  return STALE_CHUNK_MESSAGES.some((pattern) => message.includes(pattern))
}

/**
 * Storage access that tolerates being unavailable.
 *
 * `sessionStorage` throws rather than returning null when cookies are blocked or
 * the page is sandboxed. A recovery path must not itself throw, so both
 * accessors degrade to "no marker recorded" — which fails toward attempting the
 * reload, since being stuck on an error page is the worse outcome.
 */
function readReloadMarker(): number | null {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_MARKER_KEY)
    if (!raw) return null
    const parsed = Number.parseInt(raw, 10)
    return Number.isFinite(parsed) ? parsed : null
  } catch {
    return null
  }
}

function writeReloadMarker(at: number): void {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER_KEY, String(at))
  } catch {
    // Storage unavailable — the reload still helps, it just isn't rate-limited.
  }
}

/**
 * Whether this page instance has already triggered its reload and is waiting for
 * the navigation to commit.
 *
 * Distinct from the stored marker on purpose. The marker survives the reload and
 * answers "did a PREVIOUS page try this and end up here anyway?" — which means
 * recovery failed. This answers "is a reload already on its way right now?", so
 * a second report of the same failure waits quietly instead of either reloading
 * twice or mistaking an in-flight recovery for a failed one.
 */
let reloadInFlight = false

export function isStaleChunkReloadInFlight(): boolean {
  return reloadInFlight
}

/** Test seam — a module-level flag would otherwise leak between cases. */
export function resetStaleChunkReloadInFlight(): void {
  reloadInFlight = false
}

/**
 * True when recovery has already been tried and this page is STILL failing.
 *
 * This is the reportable case, and it is the only one. A stale chunk that
 * reloads onto the new build is a deploy working as designed — the user sees a
 * spinner and lands on the current version — so reporting it spends quota on a
 * non-event. It was 49% of live frontend Sentry volume (NEX-3948). A stale chunk
 * that survives its own reload is the opposite: the new build is not serving the
 * assets its own document asks for, which is a broken deploy and genuinely ours.
 *
 * The two are told apart by the pair, never by either half alone:
 *
 * - `reloadInFlight` is per PAGE INSTANCE, so it is true only in the document
 *   that started the reload — a recovery on its way.
 * - the stored marker SURVIVES the reload, so finding it in a fresh document
 *   means a previous page tried this and we ended up here anyway.
 *
 * The marker alone cannot answer it: it is equally present a millisecond after
 * the first attempt and after a failed one. That is why this reads both.
 */
export function hasStaleChunkRecoveryFailed({ now = Date.now() }: { now?: number } = {}): boolean {
  if (reloadInFlight) return false

  const lastAttempt = readReloadMarker()

  return lastAttempt !== null && now - lastAttempt < RELOAD_COOLDOWN_MS
}

export interface StaleChunkReloadOptions {
  /** Injected for tests; defaults to the real clock. */
  now?: number
  /** Injected for tests; defaults to a real page reload. */
  reload?: () => void
}

/**
 * Reloads the page to pick up the current build, at most once per cooldown.
 *
 * Returns `true` when a reload was triggered, `false` when the guard suppressed
 * it — a `false` return means recovery already failed once and the caller should
 * surface something to the user instead of retrying.
 */
export function attemptStaleChunkReload({
  now = Date.now(),
  reload = () => window.location.reload()
}: StaleChunkReloadOptions = {}): boolean {
  const lastAttempt = readReloadMarker()

  if (lastAttempt !== null && now - lastAttempt < RELOAD_COOLDOWN_MS) return false

  writeReloadMarker(now)
  reloadInFlight = true
  reload()
  return true
}
